import { useTemplateUIRules } from "components/ProjectForm/context/TemplateUIRulesContext"
import { Countries } from "enums/dropdownValues"
import { useGetCitiesQuery, useLazyGetCountryStatesQuery } from "pages/Common/commonService"
import { SitesObj, useGetAddedByForSitesQuery, useLazyGetAllSitesQuery } from "pages/Sites/SiteService"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

export type SiteOption = {
    id: number
    name: string
    site_id: string
    pincode: number
    address: string
    state: string
    state_id: number
    city: string
    city_id: number
    country_id: number
    latitude: number
    longitude: number
}

type DropdownOption = {
    id: number
    title: string
}

export type SiteFilters = {
    search_site_name: string
    search_pincode: string
    selectedCountry: DropdownOption | null
    selectedState: DropdownOption[]
    selectedCity: DropdownOption[]
    addedBy: DropdownOption[]
}

const ITEMS_PER_PAGE = 40

/**
 * `warehouseFirst` normally comes from the deal template's UI rules. Callers mounted outside a
 * TemplateUIRulesProvider (the logistics deal detail, for one) have no template to read it from,
 * so they pass the value in instead of silently falling back to the provider's `false` default.
 */
const useSites = (warehouseFirstOverride?: boolean) => {
    const [getAllSites, { isLoading: isSitesLoading }] = useLazyGetAllSitesQuery()
    const [getCountryStates, { data: statesList }] = useLazyGetCountryStatesQuery()
    const { data: addedByList } = useGetAddedByForSitesQuery()
    const {
        isFetching: isFetchingUIRules,
        accountSitesRules: { warehouse_first: templateWarehouseFirst },
    } = useTemplateUIRules()
    const warehouseFirst = warehouseFirstOverride ?? templateWarehouseFirst

    const [filters, setFilters] = useState<SiteFilters>({
        search_site_name: "",
        search_pincode: "",
        selectedCountry: { id: 101, title: "India" },
        selectedState: [],
        selectedCity: [],
        addedBy: [],
    })

    const [currentPage, setCurrentPage] = useState(1)
    const [allSites, setAllSites] = useState<SitesObj[]>([])
    const [totalPages, setTotalPages] = useState(1)
    const [isLoadingMore, setIsLoadingMore] = useState(false)
    const isInitialFetch = useRef(true)

    const { data: citiesList } = useGetCitiesQuery(
        {
            state_id: filters.selectedState?.map(i => i.id),
        },
        {
            skip: !filters.selectedState?.length,
        }
    )

    useEffect(() => {
        if (filters.selectedCountry?.id) {
            getCountryStates({ country_id: filters.selectedCountry.id })
        }
    }, [filters.selectedCountry?.id, getCountryStates])

    // Fetches sites with pagination, optionally resetting the list for filter changes
    const fetchSites = useCallback(
        async (page: number, resetList: boolean = false) => {
            const { search_site_name, search_pincode, selectedState, selectedCity, addedBy, selectedCountry } = filters
            const stateFilter = JSON.stringify(selectedState?.length ? selectedState.map(i => i.id) : [])
            const cityFilter = JSON.stringify(selectedCity?.length ? selectedCity.map(i => i.id) : [])
            const addedByFilter = JSON.stringify(addedBy?.length ? addedBy.map(i => i.id) : [])

            try {
                const result = await getAllSites({
                    page,
                    per_page: ITEMS_PER_PAGE,
                    state: stateFilter,
                    city: cityFilter,
                  site: search_site_name,
                    pincode: search_pincode,
                    added_by: addedByFilter,
                    country_id: selectedCountry?.id,
                    ...(warehouseFirst && { warehouse_sort: true }),
                }).unwrap()

                if (resetList) {
                    setAllSites(result.data || [])
                } else {
                    setAllSites(prev => [...prev, ...(result.data || [])])
                }
                setTotalPages(result.totalPages || 1)
            } catch (error) {
                console.error("Error fetching sites:", error)
            }
        },
        [filters, getAllSites, warehouseFirst]
    )

    useEffect(() => {
        if (isFetchingUIRules) return
        setCurrentPage(1)
        setAllSites([])
        isInitialFetch.current = true
        fetchSites(1, true)
    }, [
        filters.search_pincode,
        filters.search_site_name,
        filters.addedBy,
        filters.selectedState,
        filters.selectedCity,
        filters.selectedCountry?.id,
        isFetchingUIRules,
        warehouseFirst,
    ])

    const hasMoreSites = useMemo(() => {
        return currentPage < totalPages
    }, [currentPage, totalPages])

    const totalSitesCount = useMemo(() => {
        if (currentPage >= totalPages) {
            return allSites.length
        }
        return totalPages * ITEMS_PER_PAGE
    }, [totalPages, currentPage, allSites.length])

    const handleShowMore = useCallback(async () => {
        if (isLoadingMore || !hasMoreSites) return

        setIsLoadingMore(true)
        const nextPage = currentPage + 1
        setCurrentPage(nextPage)
        await fetchSites(nextPage, false)
        setIsLoadingMore(false)
    }, [currentPage, hasMoreSites, isLoadingMore, fetchSites])

    const updateFilter = useCallback(<K extends keyof SiteFilters>(key: K, value: SiteFilters[K]) => {
        setFilters(prev => ({ ...prev, [key]: value }))
    }, [])

    const handleSearchSiteName = useCallback(
        (value: string) => {
            updateFilter("search_site_name", value)
        },
        [updateFilter]
    )

    const handleSearchPincode = useCallback(
        (value: string) => {
            updateFilter("search_pincode", value)
        },
        [updateFilter]
    )

    const handleCountryChange = useCallback((value: DropdownOption | null) => {
        setFilters(prev => ({
            ...prev,
            selectedCountry: value,
            selectedState: [],
            selectedCity: [],
        }))
    }, [])

    const handleStateChange = useCallback((value: DropdownOption[]) => {
        setFilters(prev => ({
            ...prev,
            selectedState: value,
            selectedCity: [],
        }))
    }, [])

    const handleCityChange = useCallback(
        (value: DropdownOption[]) => {
            updateFilter("selectedCity", value)
        },
        [updateFilter]
    )

    const handleAddedByChange = useCallback(
        (value: DropdownOption[]) => {
            updateFilter("addedBy", value)
        },
        [updateFilter]
    )

    const handleResetFilters = useCallback(() => {
        setFilters({
            search_site_name: "",
            search_pincode: "",
            selectedCountry: { id: 101, title: "India" },
            selectedState: [],
            selectedCity: [],
            addedBy: [],
        })
    }, [])

    const getCountryTitleById = useCallback((id: number) => {
        return Countries.find(country => country.id === id)?.title || "-"
    }, [])

    const getSiteById = useCallback(
        (siteId: number): SiteOption | null => {
            const site = allSites.find((s: SitesObj) => s.id === siteId)
            if (!site) return null

            return {
                id: site.id,
                name: site.name,
                site_id: site.site_id,
                pincode: site.pincode,
                address: site.address,
                state: site.state,
                state_id: site.state_id,
                city: site.city,
                city_id: site.city_id,
                country_id: site.country_id,
                latitude: site.latitude,
                longitude: site.longitude,
            }
        },
        [allSites]
    )

    const statesOptions = useMemo(() => {
        return statesList?.map(state => ({ id: state.id, title: state.name })) || []
    }, [statesList])

    const citiesOptions = useMemo(() => {
        return citiesList?.map(city => ({ id: city.id, title: city.name })) || []
    }, [citiesList])

    const addedByOptions = useMemo(() => {
        return addedByList?.map(obj => ({ id: obj.id, title: obj.user_name })) || []
    }, [addedByList])

    return {
        states: {
            filters,
            sitesData: allSites,
            displayedSites: allSites,
            isSitesLoading: isSitesLoading && allSites.length === 0,
            isLoadingMore,
            hasMoreSites,
            totalSitesCount,
            currentPage,
            statesOptions,
            citiesOptions,
            addedByOptions,
        },
        functions: {
            handleSearchSiteName,
            handleSearchPincode,
            handleCountryChange,
            handleStateChange,
            handleCityChange,
            handleAddedByChange,
            handleResetFilters,
            handleShowMore,
            getCountryTitleById,
            getSiteById,
            updateFilter,
        },
    }
}

export default useSites
